import React, { useState, memo } from 'react';

const MAGNIFIER_SIZE = 180;
const ZOOM_LEVEL = 2;

interface ZoomableImageProps {
  src: string;
}

export const ZoomableImage = memo<ZoomableImageProps>(({ src }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const imgRef = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRef.current) return;

    const rect = imgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMagnifierPos({ x, y });
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <div
      ref={imgRef}
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        backgroundImage: `url(${src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        cursor: isHovering ? 'none' : 'default',
        backgroundColor: '#0a0a0a',
        position: 'relative',
        overflow: 'visible',
        isolation: 'isolate',
        zIndex: isHovering ? 1000 : 1,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Overlay transparente para garantir área clicável */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'all',
        }}
      />

      {isHovering && imgRef.current && (
        <div
          style={{
            position: 'absolute',
            width: `${MAGNIFIER_SIZE}px`,
            height: `${MAGNIFIER_SIZE}px`,
            borderRadius: '50%',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 0 20px rgba(0, 0, 0, 0.3)',
            left: `${magnifierPos.x - MAGNIFIER_SIZE / 2}px`,
            top: `${magnifierPos.y - MAGNIFIER_SIZE / 2}px`,
            backgroundSize: `${imgRef.current.offsetWidth * ZOOM_LEVEL}px ${imgRef.current.offsetHeight * ZOOM_LEVEL}px`,
            backgroundPosition: `${-magnifierPos.x * ZOOM_LEVEL + MAGNIFIER_SIZE / 2}px ${-magnifierPos.y * ZOOM_LEVEL + MAGNIFIER_SIZE / 2}px`,
            zIndex: 999,
            backdropFilter: 'blur(0px)',
            opacity: 0,
            transform: 'scale(0.8)',
            animation: 'fadeInMagnifier 0.2s ease-out forwards',
          }}
        />
      )}
    </div>
  );
});

ZoomableImage.displayName = 'ZoomableImage';
